import { dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormValues, FormControlProperties } from '@omegagrid/form';
import rp, { formatDate, fromIsoDate, toIsoDate } from '@reporting/rp-app-host';
import { HelpdeskLists, REQUEST_PRIORITIES, REQUEST_TYPES, REQUEST_STATUSES } from "../types";
import { User } from '@reporting/rp-types/core';
import { Ticket, Message } from '@reporting/rp-types/helpdesk';
import { FOLLOWER_LIST_OPTIONS, followerListValue, followerNames, followerNodes, joinFollowers, loadFollowers, storedFollowers } from './followerTreeAdapter';

export class EditTicketFormAdapter extends FormAdapter {

	constructor(
		private ticket: Ticket
	) { super() }

	private _formControls: FormControlProperties[];
	// lists are used for system messages! TODO: use adapters instead, but how to get values from keys for system messages?
	private _lists: HelpdeskLists = {
		id_type: REQUEST_TYPES.map(item => ({ k: item.k, v: msg(item.v) })),
		status: REQUEST_STATUSES.map(item => ({ k: item.k, v: msg(item.v) })),
		priority: REQUEST_PRIORITIES.map(item => ({ k: item.k, v: msg(item.v) })),
		who_responsible: [],
		who_follower: []
	};

	getColumns() {
		return [
			{ width: 120 },
			{ width: null },
		];
	}

	async getControls() {
		// the people with access to the helpdesk of the company of the request (for an SA the SA users as well, as a group
		// of their own); the followers the request has stay on the list under their names, even when they lost the right
		// since - the user sees who they are and can take them off
		const followers = await loadFollowers(this.ticket.accounting);
		const keep = storedFollowers(this.ticket);
		this._lists.who_follower = followers.map(u => ({ k: u.user_name, v: u.login_name }));
		this._lists.who_follower.unshift(...keep.filter(k => !followers.some(u => u.user_name === k.k)));
		let r = 0;
		this._formControls = [
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Subject'), for: 'subject' },
			{ row: r++, col: 1, type: FormControlType.TextInput, key: 'subject', value: this.ticket.subject, readOnly: true },
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Priority'), for: 'priority' },
			{ row: r++, col: 1, type: FormControlType.List, key: 'priority', value: this.ticket.priority, readOnly: true, content: {
				sourceData: this._lists.priority, options: { selectMode: 'single', autoClose: true }
			}},
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Type'), for: 'id_type' },
			{ row: r++, col: 1, type: FormControlType.List, key: 'id_type', value: this.ticket.id_type, required: true, content: {
				sourceData: this._lists.id_type, options: { selectMode: 'single', autoClose: true }
			}},
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Follower'), for: 'who_follower' },
			{ row: r++, col: 1, type: FormControlType.List, key: 'who_follower', value: followerListValue(this.ticket.who_follower), content: {
				sourceData: followerNodes(followers, keep), options: FOLLOWER_LIST_OPTIONS
			}},
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Escalation'), for: 'escalation' },
			{ row: r++, col: 1, type: FormControlType.Checkbox, key: 'escalation', value: this.ticket.escalation },
		];
		if (rp.userProvider.isSA()) {
			const saUsers = await rp.ms('users').get<User[]>('saUsers');
			this._lists.who_responsible = saUsers.map(u => ({ k: u.user_name, v: u.login_name }));
			this._formControls.push(
				{ row: r, col: 0, type: FormControlType.Label, value: msg('Responsible'), for: 'who_responsible' },
				{ row: r++, col: 1, type: FormControlType.List, key: 'who_responsible', value: this.ticket.who_responsible, content: {
					sourceData: this._lists.who_responsible, options: { selectMode: 'single', autoClose: true, search: true }
				}},
				{ row: r, col: 0, type: FormControlType.Label, value: msg('Date expected'), for: 'date_expected' },
				{ row: r++, col: 1, type: FormControlType.CalendarDateInput, key: 'date_expected', required: false, value: fromIsoDate(this.ticket.date_expected) },
				{ row: r, col: 0, type: FormControlType.Label, value: msg('Effort (h)'), for: 'effort' },
				{ row: r++, col: 1, type: FormControlType.NumericInput, key: 'effort', required: false, value: this.ticket.effort ?? null },
				{ row: r, col: 0, type: FormControlType.Label, value: msg('Status'), for: 'status' },
				{ row: r++, col: 1, type: FormControlType.List, key: 'status', value: this.ticket.status, content: {
					sourceData: this._lists.status, options: { selectMode: 'single', autoClose: true }
				}},
			);
		}
		return this._formControls;
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const accounting = this.ticket.accounting;
		// only the fields the user can edit: a non-SA user has no status/responsible/date/effort control and sending
		// those empty used to clear the expected date; a cleared field goes as an explicit null
		// (labels are skipped - the form gives them their index as the key)
		const keys = this._formControls.filter(c => c.key && !c.readOnly && c.type !== FormControlType.Label).map(c => c.key);
		const ticket: Ticket = {
			id_request: this.ticket.id_request,
			...Object.fromEntries(keys.map(key => {
				const value = formValues[key];
				return [key, value === '' || value === undefined || Number.isNaN(value) ? null : value];
			}))
		};
		// the calendar holds a Date (the one the form opened with, or the picked one): the server takes the day
		if ('date_expected' in ticket) ticket.date_expected = toIsoDate(ticket.date_expected as unknown as Date | string);
		// the followers picked (the tuples of the multi-select list) as the server stores them: user names separated by semicolons
		if ('who_follower' in ticket) ticket.who_follower = joinFollowers(ticket.who_follower);
		const editedTicket: Ticket = await rp.ms('helpdesk').post('tickets', ticket, { accounting });
		// save system message if row was changed
		const sysMessage: Message = {
			id_request: editedTicket.id_request,
			sys_message: true,
			text: '',
		}
		for (let key in ticket) {
			const oldVal = this.ticket[key as keyof Ticket];
			let newVal = ticket[key as keyof Ticket];
			if (oldVal != newVal) {
				const formRow = this._formControls.find(row => row.for === key);
				const name = formRow ? formRow.value : key;
				if (newVal) {
					if (key === 'date_expected') {
						newVal = formatDate(fromIsoDate(newVal as string), dates.getDateFormat());
					} else if (key === 'who_follower') {
						newVal = followerNames(newVal as string, this._lists.who_follower);
					} else if (this._lists[key as keyof HelpdeskLists]) {
						const listItem = this._lists[key as keyof HelpdeskLists].find(item => item.k === newVal);
						newVal = listItem ? listItem.v : newVal;
					}
					sysMessage.text += `${msg('Change')} *${name}* → **${newVal}**. `;
				} else {
					sysMessage.text += `*${name}* ${msg('removed')}. `;
				}
			}
		}
		if (sysMessage.text !== '') {
			await rp.ms('helpdesk').post(`messages`, sysMessage, { accounting });
		}
		return true;
	}

};
